import type {ReactElement} from 'react';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {Checkbox} from '@Components/checkbox';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {OverlayTrigger} from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {doesAccountRequireReconnection, onConnectAccount, onConnectAccountForIosApp} from '@Libraries/social-media';
import {noop} from '@Utils/general.util';
import {isMobileOrIOSApp} from '@Utils/browser.util';
import styles from './social-account-list-item.module.scss';

interface SocialAccountListItemProps {
  account: SocialAccount;
  isSelected?: boolean;
  isDisabled?: boolean;
  tooltip?: SocialAccountListItemTooltip;
  onCheck?: (idSocialMediaAccount: number) => void;
  onUncheck?: (idSocialMediaAccount: number) => void;
}

export interface SocialAccountListItemTooltip {
  text: string;
  icon: string;
  classes?: string;
}

export function SocialAccountListItem({
  account,
  isSelected = false,
  isDisabled = false,
  tooltip = undefined,
  onCheck = noop,
  onUncheck = noop,
}: SocialAccountListItemProps): ReactElement<any> {
  const [isChecked, setIsChecked] = useState(isSelected);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);

  useEffect(() => {
    setIsChecked(isSelected);
  }, [isSelected]);

  const onAccountClicked = (): void => {
    const newIsChecked = !isChecked;

    setIsChecked(newIsChecked);

    if (newIsChecked) {
      onCheck(account.id);
    } else {
      onUncheck(account.id);
    }
  };

  const toolTipElement = useMemo((): ReactElement<any> | null => {
    if (!tooltip) {
      return null;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={
          <Tooltip
            id={`tooltip-overlay-${account.id}`}
            className={`_pointer-events-none ${isExtraSmallScreen || isSmallScreen ? styles.customTooltip : ''}`}
            style={{position: 'fixed'}}
          >
            {tooltip.text}
          </Tooltip>
        }
        delay={{show: 50, hide: 50}}
        placement="top"
        popperConfig={undefined}
        show={undefined}
        defaultShow={undefined}
        onHide={undefined}
        onToggle={undefined}
        target={undefined}
        flip={undefined}
        offset={undefined}
      >
        <Icon
          ref={tooltipRef}
          icon={tooltip.icon}
          className={`${styles.tooltipIcon} ${styles.actionIconPlacement} ${tooltip.classes ?? ''}`}
          size={IconSize.SIZE_ICON_20}
          type={IconType.NONE}
          shape={IconShape.NONE}
        />
      </OverlayTrigger>
    );
  }, [tooltip]);

  const getThemeClassesForRow = (): string => {
    if (doesAccountRequireReconnection(account)) {
      return styles.error;
    }

    if (isDisabled) {
      return styles.disabled;
    }

    return '';
  };

  const isRowUnselectable = (): boolean => {
    return isDisabled || doesAccountRequireReconnection(account);
  };

  const onRefreshAccount = (): void => {
    if (isMobileOrIOSApp()) {
      void onConnectAccountForIosApp(account.type, noop);
    } else {
      onConnectAccount(account.type);
    }
  };

  const getActionElement = (): ReactElement<any> | null => {
    if (doesAccountRequireReconnection(account)) {
      return (
        <Icon
          type={IconType.SECONDARY}
          shape={IconShape.SQUARE}
          size={IconSize.SIZE_ICON_16}
          icon="icon-refresh"
          className={styles.actionIconPlacement}
          ariaLabel={window.i18next.t('pmwjs_refresh')}
          onClick={onRefreshAccount}
        />
      );
    }

    if (toolTipElement) {
      return toolTipElement;
    }

    return null;
  };

  const isDisabledState = (): boolean => {
    return isDisabled && !doesAccountRequireReconnection(account);
  };

  return (
    <ClickableDiv
      className={`_cursor-pointer flex-h-row spacing-p-2 radius-4 ${getThemeClassesForRow()} ${styles.listItem} ${isChecked ? styles.selected : ''}`}
      onClick={isRowUnselectable() ? noop : onAccountClicked}
    >
      <Checkbox className="spacing-m-r-4" isChecked={isChecked} />
      <SocialAccountLabel account={account} isDisabled={isDisabledState()} isConnectionExpired={doesAccountRequireReconnection(account)} />
      {getActionElement()}
    </ClickableDiv>
  );
}
